import type { SchoolTestDomain } from '@mathflat/domain/@entities/(Content)/SchoolTest/domain'
import type { CurriculumApi } from '@mathflat/domain/@entities/Curriculum/api'
import type { RegionDomain } from '@mathflat/domain/@entities/Region/domain'
import type { AllGradeSubject, Semester } from '@mathflat/domain/@entities/SchoolSystem/schema'
import QueryString from 'qs'

import { mergeQueryString } from '@/api/_lib/mergeQueryString'
import type { SchoolGradeData } from '@/api/studentHistory.api.ts'
import { mpEventInfo } from '@/constant'
import type { DownloadWorksheetT } from '@/contentComponents/modal/Download/WorksheetDownload.service'
import type { LessonClass, Worksheet } from '@/dto'
import type { DefaultResponse, Pagination } from '@/types/common'
import type { PaginationResponse } from '@/types/common/api'
import maxios, { type CustomRequestConfigMeta } from '@/utils/axios/maxios'
import { searchAxios } from '@/utils/axios/searchAxios'

// 학습지 API
const worksheetApi = {
  // 학습지 디자인 템플릿 가져오기
  getDesignTemplate: () => {
    return maxios.get<DefaultResponse<res.WorksheetDesignTemplate[]>>(`/worksheet/design/type`, {
      params: { mp_event_name: mpEventInfo.학습지디자인템플릿조회 },
    })
  },

  /* 학습지 만들기 */
  // 학습지 만들기 - 1단계 : 단원유형별 학습지 필터 생성 - filter filterId 생성
  createFilterIdByChapter: (data: req.CreateFilterByChapter, meta?: CustomRequestConfigMeta) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>('/worksheet/filter/concept', data, {
      params: { mp_event_name: mpEventInfo['학습지만들기1_단원/유형별_학습지필터생성'] },
      meta,
    })
  },

  // 학습지 만들기 - 검색된 결과 가져오기 : filterId를 통한 학습지 옵션 검색
  getSearchFilterByFilterId: (filterId: string, meta?: CustomRequestConfigMeta) => {
    return maxios.get<DefaultResponse<res.SearchFilterByChapterFilterId>>(`/worksheet/filter`, {
      params: {
        filterId,
        mp_event_name: mpEventInfo['학습지만들기1_단원유형별(필터생성변경시마다)'],
      },
      meta,
    })
  },

  createFilterByPublicWorkbook: (
    data: req.CreateFilterByPublicWorkbook,
    meta?: CustomRequestConfigMeta,
  ) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>(
      '/worksheet/filter/public-workbook',
      data,
      {
        params: { mp_event_name: mpEventInfo.학습지만들기1_시중교재_학습지필터옵션생성 },
        meta,
      },
    )
  },

  // 학습지만들기 - 1단계 : 수능 모의고사 필터 전달 - filterId 생성
  createFilterByPracticeTest: (data: req.CreateFilterByPracticeTest) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>('/worksheet/filter/practice', data, {
      params: { mp_event_name: mpEventInfo.학습지만들기1_모의고사_필터생성변경시마다 },
    })
  },
  //TODO: [리팩토링] updateFilterByPracticeTest : createFilterByPracticeTest에서 params에 filterId가 존재하면 update임. type과 함수로 구분하기. 모든 filterId생성 함수 동일하게 다 고쳐야.

  // 학습지만들기 - 1단계 : 학교별 내신 기출 필터 전달
  createFilterBySchoolTest: (data: req.CreateFilterBySchoolTest) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>(
      '/worksheet/filter/school-test',
      data,
      {
        params: {},
      },
    )
  },
  // 학습지만들기 - 1단계 :기출 업로드 - 기타 학습지
  createFilterByCustomPaper: (data: req.CreateFilterByCustomPaper) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>(
      '/worksheet/filter/custom-paper',
      data,
      { params: { mp_event_name: mpEventInfo.학습지만들기1_기출업로드_기타학습지_필터생성 } },
    )
  },
  // 학습지만들기 - 1단계 : 기출 업로드 - 기출 시험지
  createFilterBySchoolTestPaper: (data: req.CreateFilterBySchoolTestPaper) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>(
      '/worksheet/filter/school-test-paper',
      data,
      { params: { mp_event_name: mpEventInfo.학습지만들기1_기출업로드_기출시험지_필터생성 } },
    )
  },
  // 학습지만들기 - 기출 업로드 v2 - 원본 학습지 필터 생성
  createOriginalFilterBySchoolTestPaperV2: (
    payload: req.CreateFilterBySchoolTestPaperOriginalV2,
  ) => {
    return maxios.post<{ filterId: string }>(
      '/v2/worksheet/filter/school-test-paper/original',
      payload,
      { params: { mp_event_name: mpEventInfo.학습지만들기1_기출업로드_기출시험지_필터생성 } },
    )
  },
  // 학습지만들기 - 기출 업로드 v2 - 유사 학습지 필터 생성
  createSimilarFilterBySchoolTestPaperV2: (payload: req.CreateFilterBySchoolTestPaperSimilarV2) => {
    return maxios.post<{ filterId: string }>(
      '/v2/worksheet/filter/school-test-paper/similar',
      payload,
      { params: { mp_event_name: mpEventInfo.학습지만들기1_기출업로드_기출시험지_필터생성 } },
    )
  },
  // 오답학습지 - 단원별 filterID 만들기
  createFilterIdByConcept: (payload: req.CreateFilterByWeakConcept) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>(
      '/worksheet/filter/weak-concept',
      payload,
      {
        params: { mp_event_name: mpEventInfo.단원기간별_단원별_필터생성변경시마다 },
      },
    )
  },

  // 오답학습지 - 기간별 filterID 만들기
  createFilterIdByPeriod: (data: req.CreateFilterByWeakPeriod) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>(
      '/worksheet/filter/weak-period',
      data,
      {
        params: { mp_event_name: mpEventInfo.단원기간별_기간별_필터생성변경시마다 },
      },
    )
  },

  // 오답학습지 - 학습지별 filterID 만들기
  createFilterIdByWorksheet: (data: req.CreateFilterByWeakWorksheet) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>(
      '/worksheet/filter/weak-worksheet',
      data,
      {
        params: { mp_event_name: mpEventInfo.개별오답학습지필터_필터생성변경시마다 },
      },
    )
  },

  // 원클릭학습지 - filterId 만들기
  createFilterIdByOneClick: (data: req.CreateFilterByOneClick) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>('/worksheet/filter/one-click', data, {
      params: { mp_event_name: mpEventInfo.학습지만들기_원클릭복습학습지_필터생성변경시마다 },
    })
  },

  // 학습지 병합 - filterId 만들기
  postFilterIdByMergedWorksheet: (data: req.CreateFilterByMerge) => {
    return maxios.post<res.FilterId>('/worksheet/filter/merged-worksheet', data)
  },

  postGroupWrongWorksheetPerStudent: (data: req.CreateEachStudentWorksheet) => {
    return maxios.post<DefaultResponse<Worksheet['id']>>('/worksheet/group', data, {
      params: { mp_event_name: mpEventInfo.개별오답학습지필터_필터생성변경시마다 },
    })
  },

  // 학습지 만들기 - 유사문제 검색
  postSearchWorksheetSimilarProblem: (
    data: req.SearchSimilarProblem,
    meta?: CustomRequestConfigMeta,
  ) => {
    return maxios.post<DefaultResponse<res.MakeWorksheetProblem[]>>(`/worksheet/problem`, data, {
      params: { mp_event_name: mpEventInfo.학습지만들기1_학습지문제수조회 },
      meta,
    })
  },
  // 학습지 만들기 - 2단계 : 내 문제 > 나의DB 탭 문제 목록
  postMyDbFilterProblem: (data: req.MyDbFilterProblem) => {
    return maxios.post<res.MakeWorksheetProblem[]>('/worksheet/filter/my-db/problem', data)
  },
  // 학습지 만들기 - 1단계 : filterId를 통해 학습지 문제 수 조회
  getProblemCountByFilterId: (params: { filterId: string }, meta?: CustomRequestConfigMeta) => {
    return maxios.get<DefaultResponse<res.ProblemCountByFilterId>>(`/worksheet/problem/count`, {
      params,
      meta,
    })
  },
  // 학습지 만들기 - 3단계 : 학습지 최종 생성하기 - 복제후 수정
  createWorksheet: (data: req.CreateWorksheet, meta?: CustomRequestConfigMeta) => {
    return maxios.post<DefaultResponse<Worksheet['id']>>('/worksheet', data, {
      params: { mp_event_name: mpEventInfo['학습지만들기3_학습지만들기버튼선택시'] },
      meta,
    })
  },
  // 학습지형 입학고사 최종 생성. 200 응답이 CommonResponse 래핑 없이 본문을 직접 반환한다
  createEntranceTestWorksheet: (
    data: req.CreateEntranceWorksheet,
    meta?: CustomRequestConfigMeta,
  ) => {
    return maxios.post<res.CreateEntranceWorksheet>('/creverse/entrance-worksheet', data, {
      meta,
    })
  },
  // GET /creverse/entrance-worksheet — 학습지형 입학고사 목록 조회
  getEntranceTestWorksheetList: (params: string) => {
    return maxios.get<PaginationResponse<res.CreverseEntranceWorksheetResponse[]>>(
      `/creverse/entrance-worksheet${params}`,
    )
  },
  // DELETE /creverse/entrance-worksheet — 학습지형 입학고사 삭제(휴지통 이동), 단건도 배열 1개
  deleteEntranceTestWorksheets: (data: req.EntranceWorksheetBulkDeleteRequest) => {
    return maxios.delete<DefaultResponse<null>>('/creverse/entrance-worksheet', { data })
  },
  // DELETE /creverse/entrance-worksheet/force-delete — 학원그룹(센터간 공유) 입학고사 강제 삭제(휴지통 미경유, 다건)
  forceDeleteEntranceTestWorksheets: (data: req.EntranceWorksheetBulkDeleteRequest) => {
    return maxios.delete<DefaultResponse<null>>('/creverse/entrance-worksheet/force-delete', {
      data,
    })
  },
  // POST /creverse/entrance-worksheet/{id}/copy — 센터 시험지를 센터 공유로 승격(복제 후 생성, 원본 유지)
  postEntranceTestWorksheetCopy: (
    worksheetId: res.CreverseEntranceWorksheetResponse['id'],
    data: req.EntranceWorksheetCopyRequest,
  ) => {
    return maxios.post<res.CreateEntranceWorksheet>(
      `/creverse/entrance-worksheet/${worksheetId}/copy`,
      data,
    )
  },
  // POST /creverse/entrance-worksheet/assignments — 이미 배정된 (시험지×게스트) 조합은 서버가 스킵
  assignEntranceTestWorksheets: (data: req.EntranceWorksheetAssignmentRequest) => {
    return maxios.post<res.EntranceWorksheetAssignEcho>(
      '/creverse/entrance-worksheet/assignments',
      data,
    )
  },
  // DELETE /creverse/entrance-worksheet/assignments — 무효 건이 섞이면 서버가 전체 롤백
  unassignEntranceTestWorksheets: (data: req.EntranceWorksheetAssignmentCancellationRequest) => {
    return maxios.delete<DefaultResponse<null>>('/creverse/entrance-worksheet/assignments', {
      data,
    })
  },
  // 미배정 시험지 전체 수정 — 배정된 시험지는 서버가 거부
  putEntranceTestWorksheet: (
    worksheetId: res.CreverseEntranceWorksheetResponse['id'],
    data: req.UpdateEntranceWorksheet,
  ) => {
    return maxios.put<unknown>(`/creverse/entrance-worksheet/${worksheetId}`, data)
  },
  // 배정된 시험지의 3단계 옵션만 수정 — 서버가 tag·prefix를 고정하므로 제거해 보낸다
  patchEntranceTestWorksheet: (
    worksheetId: res.CreverseEntranceWorksheetResponse['id'],
    data: Omit<req.EditAssignedWorksheet, 'tag' | 'prefix'>,
  ) => {
    return maxios.patch<unknown>(`/creverse/entrance-worksheet/${worksheetId}`, data)
  },
  // 학습지 만들기 - 학습지 수정하기
  putWorksheetFix: (worksheetId: Worksheet['id'], data: Omit<req.CreateWorksheet, 'shareScope'>) => {
    return maxios.put<DefaultResponse<null>>(`/worksheet/${worksheetId}`, data, {
      params: { mp_event_name: mpEventInfo.학습지만들기3_학습지수정하기선택시 },
    })
  },
  // 학습지 센터 공유로 복제
  postWorksheetCopy: (worksheetId: number, data: req.CopyWorksheet) => {
    return maxios.post<DefaultResponse<null>>(`/worksheet/${worksheetId}/copy`, data)
  },
  // 학습지 만들기 - 출제된 학습지 수정하기
  patchWorksheetFix: (worksheetId: number, data: req.EditAssignedWorksheet) => {
    return maxios.patch<DefaultResponse<null>>(`/worksheet/${worksheetId}`, data, {
      params: { mp_event_name: mpEventInfo.학습지만들기3_출제된학습지수정하기선택시 },
    })
  },
  /* 학습지 디자인 */
  // 학습지 디자인 - 학습진 디자인 리스트 가져오기
  getWorksheetDesign: (queryString: string) => {
    return maxios.get<Pagination<res.WorksheetDesign[]>>(`/worksheet/design${queryString}`, {
      params: { mp_event_name: mpEventInfo.학습지만들기3_학습지디자인템플릿조회 },
    })
  },
  // 학습지 디자인 - 학습지 디자인 가져오기
  getWorksheetDesignDetail: (id: number) => {
    return maxios.get<DefaultResponse<res.WorksheetDesignDetail>>(`/worksheet/design/${id}`, {
      params: { mp_event_name: mpEventInfo.학습지만들기3_특정학습지디자인템플릿조회 },
    })
  },
  // 학습지 디자인 - 학습지 디자인 생성
  postWorksheetDesignDetail: (data: req.WorksheetDesignDetail) => {
    return maxios.post(`/worksheet/design`, data, {
      params: { mp_event_name: mpEventInfo.학습지만들기3_학습지템플릿생성 },
    })
  },
  // 학습지 디자인 - 학습지 디자인 수정
  putWorksheetDesignDetail: (id, data: req.WorksheetDesignDetail) => {
    return maxios.put(`/worksheet/design/${id}`, data, {
      params: { mp_event_name: mpEventInfo.학습지만들기3_학습지템플릿수정 },
    })
  },
  // 학습지 디자인 -학습지 디자인 삭제
  deleteWorksheetDesignDetail: (id: number) => {
    return maxios.delete(`/worksheet/design/${id}`, {
      params: { mp_event_name: mpEventInfo.학습지만들기3_학습지템플릿삭제 },
    })
  },
  // 기관 학습지 가져오기
  getMyWorksheet: (params: string) => {
    return maxios.get<AcademyResponse>(`/worksheet/academy${params}`, {
      params: { mp_event_name: mpEventInfo.내학습지조회 },
    })
  },
  // 내신대비 학습지 가져오기
  getRecommend: (params: string) => {
    return maxios.get<RecommendResponse>(
      // exposition - mockup 전용, 1은 2개 아이템 존재, 2는 비어있는 리스트 반환
      `/worksheet/school-prepare${params}`,
      {
        params: { mp_event_name: mpEventInfo.내신대비추천학습지조회 },
      },
    )
  },
  // 내신대비 서술형 학습지 가져오기
  getEssayWorksheet: (params: string) => {
    return maxios.get<EssayWorksheetResponse>(`/worksheet/school-prepare-essay${params}`)
  },
  // 내신대비 교과서 (쌍둥이)학습지 가져오기
  getTextbookWorksheet: (params: string) => {
    return maxios.get<Pagination<res.TextbookWorksheet[]>>(
      `/worksheet/school-prepare-textbook${params}`,
      {
        params: { mp_event_name: mpEventInfo.내신대비교과서학습지조회 },
      },
    )
  },
  // 모의고사 학습지 가져오기
  getPracticeTest: (params: string) => {
    return maxios.get<PracticeTestResponse>(`/worksheet/practice-test${params}`, {
      params: { mp_event_name: mpEventInfo.수능모의고사학습지조회 },
    })
  },
  // 입학테스트 학습지 가져오기
  getEntranceTest: (params: string) => {
    return maxios.get<Pagination<res.EntranceTestWorksheet[]>>(`/worksheet/entrance${params}`, {
      params: { mp_event_name: mpEventInfo.입학TEST학습지조회 },
    })
  },
  // 연산 학습지 가져오기
  getLegacyArithmetic: (params: req.LegacyArithmetic) => {
    return maxios.get<LegacyArithmeticResponse>(`/worksheet/arithmetic`, {
      params: {
        ...params,
        params: { mp_event_name: mpEventInfo.연산학습지조회_구 },
      },
    })
  },
  // 다른 기관 학습지 가져오기
  getShared: (params: req.GetWorksheetShared) => {
    return searchAxios.get<ShareResponse>(`/v1/shared-custom-worksheets`, {
      params: {
        ...params,
        // 검색 API 필수 파라미터. 목록은 facet/정확한 총건수를 쓰지 않으므로 둘 다 false.
        includeAggregations: false,
        exactTotal: false,
        mp_event_name: mpEventInfo.다른기관학습지조회,
      },
    })
  },
  // 단원 테스트 학습지 가져오기
  getChapterTest: (queryString: string) => {
    return maxios.get<Pagination<res.ChapterTestWorksheet[]>>(`/worksheet/chapter${queryString}`, {
      params: { mp_event_name: mpEventInfo.단원TEST학습지조회 },
    })
  },
  // 주간 테스트 학습지 가져오기
  getWeeklyTest: (queryString: string) => {
    return maxios.get<Pagination<res.WeeklyTestWorksheet[]>>(`/worksheet/weekly${queryString}`, {
      params: { mp_event_name: mpEventInfo.주간TEST학습지조회 },
    })
  },
  // KMM수학경시대회 학습지 가져오기
  // TODO:  data tracking 변경 필요
  getKMMExam: (queryString: string) => {
    return maxios.get(`/worksheet/exam${queryString}`, {
      // params: { mp_event_name: fpEventInfo.학력평가_원본시험지조회 },
    })
  },
  // 수능특강, 수능완성 학습지 가져오기
  getSatWorksheet: (queryString: string) => {
    return maxios.get(`/worksheet/sat${queryString}`, {
      params: { mp_event_name: mpEventInfo['수능특강/완성_목록_조회'] },
    })
  },
  // 경찰대, 사관학교 학습지 가져오기
  getPoliceMilitaryPracticeTest: (queryString: string) => {
    return maxios.get<Pagination<res.PoliceMilitaryPracticeWorksheet[]>>(
      `/worksheet/university-practice-test${queryString}`,
    )
  },
  // 경찰대, 사관학교 쌍둥이 학습지 가져오기
  getPoliceMilitaryPracticeTestPair: (queryString: string) => {
    return maxios.get<Pagination<res.PoliceMilitaryPracticeWorksheet[]>>(
      `/worksheet/university-practice-test/pair${queryString}`,
    )
  },
  // 학습지 조회
  getWorksheetDetail: <T extends 'WEAK_WORKSHEET' | 'WORKSHEET' = 'WORKSHEET'>(
    worksheetId: number,
    ignoredForDeleted?: boolean,
  ) => {
    const params = QueryString.stringify(
      {
        ignoredForDeleted,
      },
      { addQueryPrefix: true },
    )

    return maxios.get<DefaultResponse<res.WorksheetDetail<T>>>(
      `/worksheet/${worksheetId}${params}`,
      {
        params: { mp_event_name: mpEventInfo.학습지상세조회 },
      },
    )
  },
  getAssignedStudentInfoByWorksheetId: (params: req.AssignedStudentsByWorksheetIdParams) => {
    return maxios.get<DefaultResponse<res.AssignedStudents>>(`/worksheet/${params.id}/assign`, {
      params: { mp_event_name: mpEventInfo.학습지다운로드 }, // TODO 연산 FP
    })
  },

  getWorksheetDownload: (
    params: req.WorksheetDownloadParams,
    type?: DownloadWorksheetT['type'],
  ) => {
    return maxios.get<common.PresignedFile>(`/worksheet/v2/download`, {
      params: {
        ...params,
        ...(type !== 'THEME_ARITHMETIC' &&
          type !== 'THEME_ARITHMETIC_ELEMENTARY' && { mp_event_name: mpEventInfo.학습지다운로드 }),
      },
    })
  },
  deleteWorksheets: (worksheetIds: number[]) => {
    return maxios({
      url: '/worksheet',
      method: 'DELETE',
      data: worksheetIds,
      params: { mp_event_name: mpEventInfo.학습지삭제 },
    })
  },
  // DELETE /worksheet/force-delete — 학원그룹 학습지 강제 삭제(휴지통 미경유, 다건)
  forceDeleteWorksheets: (worksheetIds: Worksheet['id'][]) => {
    return maxios({
      url: '/worksheet/force-delete',
      method: 'DELETE',
      data: worksheetIds,
    })
  },
  deleteWorksheetsInTrash: (worksheetIds: Worksheet['id'][]) => {
    return maxios({
      url: '/worksheet/trash/by-worksheet',
      method: 'DELETE',
      data: worksheetIds,
    })
  },
  emptyWorksheetInTrash: () => {
    return maxios({
      url: '/worksheet/trash/by-academy',
      method: 'DELETE',
    })
  },
  patchWorksheetShared: (data: req.PatchWorksheetShared) => {
    return maxios.patch<req.PatchWorksheetSharedResponse>('/worksheet/shared/v2', data, {
      params: { mp_event_name: mpEventInfo.학습지공개여부수정 },
    })
  },

  getChapters: async (params: req.CurriculumChapters) => {
    return maxios.get<DefaultResponse<CurriculumApi.Response.CurriculumInfo[]>>(
      `/curriculums/nested`,
      {
        params: { ...params, mp_event_name: mpEventInfo.학습지만들기1단계_범위조회필터 },
      },
    )
  },
  getArithmeticWorksheetsByCurriculumKey: async (params: req.CurriculumGroups) => {
    return maxios.get<Pagination<res.WorksheetsByCurriculumKey>>(`/worksheet/arithmetic/group`, {
      params: {
        ...params,
        mp_event_name: mpEventInfo.연산학습지조회,
      },
    })
  },
  patchAccessModifier: async (requestBody: req.AccessModifier) => {
    return maxios.patch(`/worksheet`, requestBody, {
      params: {
        mp_event_name: mpEventInfo.학습지학생앱공개여부수정,
      },
    })
  },
  getGradeXLS: async (
    { schoolType, grade }: SchoolGradeData,
    dateParams: req.DownloadHistoryParams,
  ) => {
    return maxios.get(`/student-worksheet/grade/${schoolType}/${grade}/xlsx`, {
      responseType: 'blob',
      params: { ...dateParams },
    })
  },
  getClassXLS: async (classId: LessonClass['id'], dateParams: req.DownloadHistoryParams) => {
    return maxios.get(`/student-worksheet/lesson-class/${classId}/xlsx`, {
      responseType: 'blob',
      params: { ...dateParams },
    })
  },

  // 학교별 기출 유사 학습지 검색
  getSimilarWorksheetsBySchoolTest: async (params: req.GetSimilarWorksheetBySchoolTestParams) => {
    return maxios.get<SimilarWorksheetBySchoolTestResponse>('/worksheet/school-test/by-school', {
      params,
    })
  },

  // 학교별 기출 유사 학습지 수량 검색
  getSimilarWorksheetCountBySchoolTest: async (
    params: Omit<req.GetSimilarWorksheetBySchoolTestParams, 'searchType'>,
  ) => {
    return maxios.get<SimilarWorksheetCountBySchoolTestResponse>(
      '/worksheet/school-test/count/by-school',
      {
        params,
      },
    )
  },

  // 삭제된 학습지 목록 가져오기
  getDeletedWorksheet: (params: string) => {
    return maxios.get<AcademyResponse>(
      `/worksheet/academy${mergeQueryString(params, { searchShareScope: 'ACADEMY' })}`,
      {
        params: { mp_event_name: mpEventInfo.내학습지조회, status: 'HIDDEN' },
      },
    )
  },

  // 학습지 복구
  restoreWorksheet: (worksheetIds: number[]) => {
    return maxios.patch(`/worksheet/restore`, { worksheetIds })
  },
}

export default worksheetApi

type AcademyResponse = Pagination<res.MyWorksheet[]>
type RecommendResponse = Pagination<res.RecommendWorksheet[]>
type EssayWorksheetResponse = PaginationResponse<res.EssayWorksheet[]>
type PracticeTestResponse = Pagination<res.PracticeTestWorksheet[]>
type LegacyArithmeticResponse = Pagination<res.LegacyArithmeticWorksheet[]>
type ShareResponse = PaginationResponse<res.SharedCustomWorksheet[]>

export type SimilarWorksheetBySchoolTestResponse = SimilarWorksheetBySchoolTest[]
type SimilarWorksheetCountBySchoolTestResponse = {
  sameSchool: number
  sameRegion: number
  neighborhoodRegion: number
  specialEducationRegion: number
}

export type SchoolTestSearchType = 'SCHOOL' | 'REGION' | 'NEIGHBORHOOD' | 'SPECIAL_EDUCATION'

export type SimilarWorksheetBySchoolTest = {
  id: number
  title: string
  chapter: string
  year: number
  grade: AllGradeSubject
  semester: Semester
  schoolTestType: SchoolTestDomain.SchoolTestType
  school: RegionDomain.SchoolByRegion
  region: RegionDomain.Region
  assignList: _AssignedStudentWorksheet[]
}
